import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const VisibilityContext = createContext(null);
const messageKey = (insightId, text) => insightId && typeof text === 'string' && text.trim()
  ? JSON.stringify([String(insightId), text.trim()]) : null;

// Local to one Activities lifetime/viewer. Never share viewed messages across accounts.
export const CoachVisibilityProvider = ({ children }) => {
  const [viewed, setViewed] = useState(() => new Set());
  const markViewed = useCallback(key => setViewed(previous => {
    if (previous.has(key)) return previous;
    const next = new Set(previous).add(key);
    if (next.size > 500) next.delete(next.values().next().value);
    return next;
  }), []);
  const [dailyCues, setDailyCues] = useState(() => new Set());
  const setDailyCueVisible = useCallback((token, visible) => setDailyCues(previous => {
    if (previous.has(token) === visible) return previous;
    const next = new Set(previous);
    if (visible) next.add(token); else next.delete(token);
    return next;
  }), []);
  const value = useMemo(() => ({ viewed, markViewed, dailyCueVisible: dailyCues.size > 0, setDailyCueVisible }), [viewed, markViewed, dailyCues, setDailyCueVisible]);
  return <VisibilityContext.Provider value={value}>{children}</VisibilityContext.Provider>;
};

// Observe the actual prose, not its mounted card, avatar, or hidden typewriter reserve.
export const CoachVisibleMessage = ({ insightId, text, children, as: Element = 'p', ...props }) => {
  const ref = useRef(null);
  const context = useContext(VisibilityContext);
  const markViewed = context?.markViewed;
  const key = messageKey(insightId, text);
  const seen = context?.viewed.has(key);
  useEffect(() => {
    if (!key || seen || !markViewed || typeof IntersectionObserver === 'undefined') return undefined;
    let active = true;
    let frame = null;
    const node = ref.current;
    const uncovered = () => {
      if (typeof document.elementsFromPoint !== 'function') return true;
      const rect = node.getBoundingClientRect();
      // Ignore ONLY this exact duplicate toast; removing it exposes the prose.
      // Other toasts, modals, menus and screen overlays must not consume it.
      return [0.1, 0.5, 0.9].every(y => [0.1, 0.5, 0.9].every(x => {
        const stack = document.elementsFromPoint(rect.left + rect.width * x, rect.top + rect.height * y);
        const front = stack.find(element => (
          element.closest('[data-coach-visible-key]')?.getAttribute('data-coach-visible-key') !== key
          && !element.classList.contains('dojo-toast-container')
        ));
        return front === node || node.contains(front);
      }));
    };
    const observer = new IntersectionObserver(entries => {
      if (active && document.visibilityState !== 'hidden'
        && entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.95)
        && uncovered()) {
        markViewed(key);
      }
    }, { threshold: [0, 0.95], rootMargin: '-64px 0px -16px 0px' });
    const reobserve = () => {
      if (!active || document.visibilityState === 'hidden') return;
      observer.disconnect();
      observer.observe(node);
    };
    // Re-observe after foregrounding or an overlay-dismiss interaction. One
    // frame per event burst; no polling, DOM-wide mutation observer or idle loop.
    const afterInteraction = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => { frame = null; reobserve(); });
    };
    observer.observe(node);
    document.addEventListener('visibilitychange', reobserve);
    document.addEventListener('pointerup', afterInteraction);
    document.addEventListener('keyup', afterInteraction);
    window.addEventListener('scroll', afterInteraction, true);
    return () => {
      active = false;
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', reobserve);
      document.removeEventListener('pointerup', afterInteraction);
      document.removeEventListener('keyup', afterInteraction);
      window.removeEventListener('scroll', afterInteraction, true);
    };
  }, [key, seen, markViewed]);
  return <Element {...props} ref={ref}>{children || text}</Element>;
};

// Pending ownership is reversible; unlike saved prose it is never remembered.
export const DailyInsightCue = () => {
  const ref = useRef(null);
  const setVisible = useContext(VisibilityContext)?.setDailyCueVisible;
  useEffect(() => {
    if (!setVisible || typeof IntersectionObserver === 'undefined') return undefined;
    const token = {};
    let active = true;
    const observer = new IntersectionObserver(entries => {
      if (active) setVisible(token, document.visibilityState !== 'hidden'
        && entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.95));
    }, { threshold: [0, 0.95], rootMargin: '-64px 0px -16px 0px' });
    const reobserve = () => {
      setVisible(token, false);
      observer.disconnect();
      if (document.visibilityState !== 'hidden') observer.observe(ref.current);
    };
    observer.observe(ref.current);
    document.addEventListener('visibilitychange', reobserve);
    return () => {
      active = false;
      observer.disconnect();
      document.removeEventListener('visibilitychange', reobserve);
      setVisible(token, false);
    };
  }, [setVisible]);
  return <div ref={ref} className="daily-insight-cue" role="status">
    <span>Preparing your daily insight</span>
    <span className="coach-inline-typing" aria-hidden="true"><span /><span /><span /></span>
  </div>;
};

export const DailyInsightToastGate = ({ dailyPending, unrelatedPending, children }) => {
  const context = useContext(VisibilityContext);
  return unrelatedPending || (dailyPending && !context?.dailyCueVisible) ? children : null;
};

export const CoachToastGate = ({ insightId, text, typing = false, children }) => {
  const context = useContext(VisibilityContext);
  const key = messageKey(insightId, text);
  return !typing && context?.viewed.has(key) ? null : React.cloneElement(children, { 'data-coach-visible-key': key });
};
